/*
 * "Bekijk aan je muur" (AR) – gedeeld door ArtifyCanvas en Wandkraft
 *
 * Bouwt in de browser een 3D-model van het gekozen werk op ware grootte
 * (maat, vorm, materiaal en lijst uit de variant) en opent het in AR:
 *   - iPhone/iPad: AR Quick Look met een zelf gegenereerde USDZ (verticaal = muur)
 *   - Android:     WebXR via <model-viewer> met ar-placement="wall"
 *   - Desktop:     3D-voorbeeld + hint om de pagina op de telefoon te openen
 */
(function () {
  'use strict';

  var MODEL_VIEWER_SRC = 'https://cdn.jsdelivr.net/npm/@google/model-viewer@4.3.1/dist/model-viewer.min.js';
  var TEXTURE_MAX_PX = 2048;
  var JPEG_QUALITY = 0.9;
  var CANVAS_DEPTH_M = 0.025; // dikte spieraam
  var EDGE_WRAP = 0.012; // deel van de afbeelding dat als spiegelrand op de zijkant komt
  var FRAME = { face: 0.012, gap: 0.005, ledge: 0.004, lip: 0.003 }; // baklijst, in meters
  var ROUND_SEGMENTS = 96;

  // depth = dikte plaat, standoff = afstand tot de muur (verborgen ophangsysteem),
  // wrap = beeld loopt door over de zijkant, edge = kleur zijkant bij dunne platen.
  var MATERIALS = {
    canvas: { depth: 0.025, standoff: 0, wrap: true, rough: 0.85 },
    felt: { depth: 0.02, standoff: 0, wrap: true, rough: 1 },
    alu: { depth: 0.003, standoff: 0.015, rough: 0.6, edge: [0.86, 0.86, 0.86] },
    glass: { depth: 0.004, standoff: 0.015, rough: 0.08, edge: [0.45, 0.6, 0.55] },
    acrylic: { depth: 0.005, standoff: 0.015, rough: 0.12, edge: [0.85, 0.9, 0.9] },
    wood: { depth: 0.018, standoff: 0.01, rough: 0.8, edge: [0.62, 0.47, 0.3] }
  };

  var COLORS = {
    back: [0.93, 0.9, 0.84],
    gap: [0.06, 0.06, 0.06],
    black: [0.02, 0.02, 0.02],
    oak: [0.73, 0.56, 0.38],
    white: [0.95, 0.95, 0.93],
    chestnut: [0.16, 0.075, 0.035],
    wenge: [0.045, 0.025, 0.015],
    walnut: [0.11, 0.055, 0.028],
    teak: [0.36, 0.19, 0.08],
    blank: [0.8, 0.66, 0.48]
  };

  var cache = {};
  var modelViewerPromise = null;

  /* ---------- variant → afmetingen ---------- */

  function detectMaterial(text) {
    if (/dibond|alumin|forex/i.test(text)) return 'alu';
    if (/plexi|acryl/i.test(text)) return 'acrylic';
    if (/glas|glass|verre/i.test(text)) return 'glass';
    if (/vilt|felt|filz|feutre|akoest|akust|acoust/i.test(text)) return 'felt';
    if (/canvas|leinwand|toile/i.test(text)) return 'canvas';
    if (/\bhout\b|holz|wood|bois/i.test(text)) return 'wood';
    return null;
  }

  function detectFrame(text) {
    if (/zonder|without|ohne|sans|flat on|profiel|profil/i.test(text)) return 'none';
    if (/zwart|black|schwarz|noir/i.test(text)) return 'black';
    if (/kastanje|chestnut|kastanie|ch[aâ]taign/i.test(text)) return 'chestnut';
    if (/weng[eé]/i.test(text)) return 'wenge';
    if (/walno|walnut|nussbaum|noyer/i.test(text)) return 'walnut';
    if (/teak|teck/i.test(text)) return 'teak';
    if (/\bblank\b|onbehandeld|\braw\b|\broh\b/i.test(text)) return 'blank';
    if (/natu|eik|eiche|oak|hout|holz|wood|bois/i.test(text)) return 'oak';
    if (/\bwit\b|white|wei(ß|ss)|blanc/i.test(text)) return 'white';
    return null;
  }

  // hint = sjabloon/producttype, gebruikt als het materiaal niet in de variant staat.
  function parseVariant(options, hint) {
    var spec = { shape: 'rect', size: null, frame: 'none', material: null };
    options.forEach(function (value) {
      var text = String(value);
      var unitOf = function (u) { return (u || 'cm').toLowerCase() === 'mm' ? 0.001 : 0.01; };
      var num = function (s) { return parseFloat(s.replace(',', '.')); };
      var rect = text.match(/(\d+(?:[.,]\d+)?)\s*[x×]\s*(\d+(?:[.,]\d+)?)\s*(mm|cm)?/i);
      var round = text.match(/[Øø⌀]\s*(\d+(?:[.,]\d+)?)\s*(mm|cm)?/);
      if (rect && !spec.size) {
        spec.size = { w: num(rect[1]) * unitOf(rect[3]), h: num(rect[2]) * unitOf(rect[3]) };
      } else if (round && !spec.size) {
        var d = num(round[1]) * unitOf(round[2]);
        spec.shape = 'round';
        spec.size = { w: d, h: d };
      }
      // "Aluminiumprofiel" is een ophangsysteem, geen materiaal
      var isMount = /profiel|profil|flat on|baklijst|lijst|rahmen|cadre|frame/i.test(text);
      var frame = isMount && detectFrame(text);
      if (frame) spec.frame = frame;
      if (!isMount && !spec.material) spec.material = detectMaterial(text);
    });
    spec.material = spec.material || detectMaterial(hint || '') || (spec.shape === 'round' ? 'alu' : 'canvas');
    return spec;
  }

  function orientSize(size, imgW, imgH) {
    var imgLandscape = imgW > imgH;
    var sizeLandscape = size.w > size.h;
    if (imgW !== imgH && size.w !== size.h && imgLandscape !== sizeLandscape) {
      return { w: size.h, h: size.w };
    }
    return size;
  }

  /* ---------- textuur ---------- */

  function loadBitmap(url) {
    return fetch(new URL(url, location.href).href, { mode: 'cors' })
      .then(function (r) {
        if (!r.ok) throw new Error('Afbeelding laden mislukt (' + r.status + ')');
        return r.blob();
      })
      .then(function (blob) { return createImageBitmap(blob); });
  }

  var ANALYSIS_PX = 256;
  var OPAQUE_ALPHA = 200; // halftransparante slagschaduw telt niet mee
  var FRAME_MAX_SHARE = 0.08; // een lijst op de foto is nooit breder dan 8% van de foto
  var FRAME_COLOR_DIST = 40;

  // Vindt het deel van de foto met het kunstwerk. Vrijstaande productfoto's (PNG met
  // transparante achtergrond, slagschaduw en soms een lijst) worden bijgesneden; de lijst
  // tekent het 3D-model zelf.
  function contentRect(bitmap, trimFrame) {
    var full = { x: 0, y: 0, w: bitmap.width, h: bitmap.height };
    var s = Math.min(1, ANALYSIS_PX / Math.max(bitmap.width, bitmap.height));
    var w = Math.max(1, Math.round(bitmap.width * s)), h = Math.max(1, Math.round(bitmap.height * s));
    var c = document.createElement('canvas');
    c.width = w;
    c.height = h;
    var ctx = c.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(bitmap, 0, 0, w, h);
    var d = ctx.getImageData(0, 0, w, h).data;
    var px = function (x, y) { var i = (y * w + x) * 4; return [d[i], d[i + 1], d[i + 2], d[i + 3]]; };

    var x0 = w, y0 = h, x1 = -1, y1 = -1;
    for (var y = 0; y < h; y++) {
      for (var x = 0; x < w; x++) {
        if (d[(y * w + x) * 4 + 3] > OPAQUE_ALPHA) {
          if (x < x0) x0 = x;
          if (x > x1) x1 = x;
          if (y < y0) y0 = y;
          if (y > y1) y1 = y;
        }
      }
    }
    if (x1 < 0) return full;
    var hasTransparency = x0 > 0 || y0 > 0 || x1 < w - 1 || y1 < h - 1;
    if (!hasTransparency) return full;

    if (trimFrame) {
      // Breedte van een egale band (de lijst) vanaf de rand naar binnen, gemeten op 3 lijnen.
      var band = function (sx, sy, dx, dy, along, max) {
        var widths = [0.3, 0.5, 0.7].map(function (f) {
          var ax = along === 'y' ? sx : Math.round(x0 + (x1 - x0) * f);
          var ay = along === 'y' ? Math.round(y0 + (y1 - y0) * f) : sy;
          var ref = px(ax + dx, ay + dy);
          for (var step = 1; step <= max; step++) {
            var p = px(ax + dx * step, ay + dy * step);
            var dist = Math.abs(p[0] - ref[0]) + Math.abs(p[1] - ref[1]) + Math.abs(p[2] - ref[2]);
            if (dist > FRAME_COLOR_DIST) return step;
          }
          return 0;
        }).sort(function (a, b) { return a - b; });
        return widths[1];
      };
      var maxX = Math.floor((x1 - x0) * FRAME_MAX_SHARE), maxY = Math.floor((y1 - y0) * FRAME_MAX_SHARE);
      var left = band(x0, 0, 1, 0, 'y', maxX), right = band(x1, 0, -1, 0, 'y', maxX);
      var top = band(0, y0, 0, 1, 'x', maxY), bottom = band(0, y1, 0, -1, 'x', maxY);
      // Alleen bijsnijden als aan alle kanten een lijst is gevonden.
      if (left && right && top && bottom) {
        x0 += left; x1 -= right; y0 += top; y1 -= bottom;
      }
    }
    return { x: x0 / s, y: y0 / s, w: (x1 - x0 + 1) / s, h: (y1 - y0 + 1) / s };
  }

  // Uitsnede (cover) naar de verhouding van het werk, in pixels van de bitmap.
  function coverCrop(bitmap, aspect, trimFrame) {
    var r = contentRect(bitmap, trimFrame);
    var cw = r.w, ch = r.h, cx = r.x, cy = r.y;
    if (r.w / r.h > aspect) { cw = r.h * aspect; cx += (r.w - cw) / 2; }
    else { ch = r.w / aspect; cy += (r.h - ch) / 2; }
    return { x: cx, y: cy, w: cw, h: ch };
  }

  // Snijdt het kunstwerk bij (cover) naar de verhouding van het werk.
  function buildTexture(bitmap, aspect, crop) {
    var tw, th;
    if (aspect >= 1) { tw = TEXTURE_MAX_PX; th = Math.round(TEXTURE_MAX_PX / aspect); }
    else { th = TEXTURE_MAX_PX; tw = Math.round(TEXTURE_MAX_PX * aspect); }
    var cx = crop.x, cy = crop.y, cw = crop.w, ch = crop.h;
    var canvas = document.createElement('canvas');
    canvas.width = tw;
    canvas.height = th;
    var ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff'; // JPEG kent geen transparantie
    ctx.fillRect(0, 0, tw, th);
    ctx.drawImage(bitmap, cx, cy, cw, ch, 0, 0, tw, th);
    return new Promise(function (resolve, reject) {
      canvas.toBlob(function (blob) {
        if (!blob) return reject(new Error('Textuur maken mislukt'));
        blob.arrayBuffer().then(function (buf) { resolve(new Uint8Array(buf)); }, reject);
      }, 'image/jpeg', JPEG_QUALITY);
    });
  }

  /* ---------- geometrie ---------- */

  function createMeshBuilder() {
    var groups = {};

    function group(material) {
      if (!groups[material]) groups[material] = { positions: [], normals: [], uvs: [], indices: [] };
      return groups[material];
    }

    // 4 hoekpunten + normaal; volgorde wordt zo nodig omgedraaid zodat de voorkant naar `n` wijst.
    function quad(material, pts, n, uvs) {
      uvs = uvs || [[0, 0], [0, 0], [0, 0], [0, 0]];
      var a = pts[0], b = pts[1], c = pts[2];
      var e1 = [b[0] - a[0], b[1] - a[1], b[2] - a[2]];
      var e2 = [c[0] - a[0], c[1] - a[1], c[2] - a[2]];
      var cross = [e1[1] * e2[2] - e1[2] * e2[1], e1[2] * e2[0] - e1[0] * e2[2], e1[0] * e2[1] - e1[1] * e2[0]];
      if (cross[0] * n[0] + cross[1] * n[1] + cross[2] * n[2] < 0) {
        pts = pts.slice().reverse();
        uvs = uvs.slice().reverse();
      }
      var g = group(material);
      var base = g.positions.length / 3;
      for (var i = 0; i < 4; i++) {
        g.positions.push(pts[i][0], pts[i][1], pts[i][2]);
        g.normals.push(n[0], n[1], n[2]);
        g.uvs.push(uvs[i][0], uvs[i][1]);
      }
      g.indices.push(base, base + 1, base + 2, base, base + 2, base + 3);
    }

    function box(material, x0, x1, y0, y1, z0, z1, skipBack) {
      quad(material, [[x0, y0, z1], [x1, y0, z1], [x1, y1, z1], [x0, y1, z1]], [0, 0, 1]);
      if (!skipBack) quad(material, [[x0, y0, z0], [x1, y0, z0], [x1, y1, z0], [x0, y1, z0]], [0, 0, -1]);
      quad(material, [[x1, y0, z0], [x1, y1, z0], [x1, y1, z1], [x1, y0, z1]], [1, 0, 0]);
      quad(material, [[x0, y0, z0], [x0, y1, z0], [x0, y1, z1], [x0, y0, z1]], [-1, 0, 0]);
      quad(material, [[x0, y1, z0], [x1, y1, z0], [x1, y1, z1], [x0, y1, z1]], [0, 1, 0]);
      quad(material, [[x0, y0, z0], [x1, y0, z0], [x1, y0, z1], [x0, y0, z1]], [0, -1, 0]);
    }

    // Losse hoekpunten met eigen normalen (voor ronde vormen).
    function raw(material, positions, normals, uvs, indices) {
      var g = group(material);
      var base = g.positions.length / 3;
      Array.prototype.push.apply(g.positions, positions);
      Array.prototype.push.apply(g.normals, normals);
      Array.prototype.push.apply(g.uvs, uvs);
      indices.forEach(function (i) { g.indices.push(base + i); });
    }

    return { quad: quad, box: box, raw: raw, groups: groups };
  }

  // Coördinaten in meters; achterkant tegen de muur op z = 0, voorkant richting +z.
  // UV's volgen glTF (v = 0 bovenaan).
  function buildGeometry(spec, w, h) {
    var mat = MATERIALS[spec.material];
    var hasFrame = spec.shape === 'rect' && spec.frame !== 'none';
    // In een baklijst ligt de voorkant altijd op dezelfde diepte; dunne platen zitten op een verhoging.
    var z0 = hasFrame ? FRAME.ledge + Math.max(0, CANVAS_DEPTH_M - mat.depth) : mat.standoff;
    var z1 = z0 + mat.depth;
    var m = createMeshBuilder();
    if (spec.shape === 'round') buildRoundPanel(m, mat, w / 2, z0, z1);
    else buildRectPanel(m, mat, w, h, z0, z1);

    // verborgen ophangsysteem / verhoging achter een plaat
    if (z0 > 0.005) {
      var s = Math.min(w, h) * 0.35;
      m.box('gap', -s / 2, s / 2, -s / 2, s / 2, 0, z0, true);
    }
    if (hasFrame) buildFrame(m, w, h, z1);
    return m.groups;
  }

  function buildRectPanel(m, mat, w, h, z0, z1) {
    var x0 = -w / 2, x1 = w / 2, y0 = -h / 2, y1 = h / 2;
    var e = EDGE_WRAP;
    var side = mat.wrap ? 'art' : 'edge';

    // voorkant met kunstwerk
    m.quad('art', [[x0, y0, z1], [x1, y0, z1], [x1, y1, z1], [x0, y1, z1]], [0, 0, 1],
      [[0, 1], [1, 1], [1, 0], [0, 0]]);
    // zijkanten: gespiegelde rand van het kunstwerk (canvas/vilt) of kleur van de plaat
    m.quad(side, [[x1, y0, z1], [x1, y0, z0], [x1, y1, z0], [x1, y1, z1]], [1, 0, 0],
      [[1, 1], [1 - e, 1], [1 - e, 0], [1, 0]]);
    m.quad(side, [[x0, y0, z1], [x0, y0, z0], [x0, y1, z0], [x0, y1, z1]], [-1, 0, 0],
      [[0, 1], [e, 1], [e, 0], [0, 0]]);
    m.quad(side, [[x0, y1, z1], [x0, y1, z0], [x1, y1, z0], [x1, y1, z1]], [0, 1, 0],
      [[0, 0], [0, e], [1, e], [1, 0]]);
    m.quad(side, [[x0, y0, z1], [x0, y0, z0], [x1, y0, z0], [x1, y0, z1]], [0, -1, 0],
      [[0, 1], [0, 1 - e], [1, 1 - e], [1, 1]]);
    m.quad('back', [[x0, y0, z0], [x1, y0, z0], [x1, y1, z0], [x0, y1, z0]], [0, 0, -1]);
  }

  // Ronde plaat: textuur is vierkant bijgesneden; de cirkel gebruikt het ingeschreven deel.
  function buildRoundPanel(m, mat, r, z0, z1) {
    var n = ROUND_SEGMENTS;
    var front = { p: [0, 0, z1], nrm: [0, 0, 1], uv: [0.5, 0.5], idx: [] };
    var back = { p: [0, 0, z0], nrm: [0, 0, -1], uv: [0, 0], idx: [] };
    var side = { p: [], nrm: [], uv: [], idx: [] };
    for (var i = 0; i <= n; i++) {
      var t = (i / n) * Math.PI * 2;
      var c = Math.cos(t), s = Math.sin(t);
      front.p.push(r * c, r * s, z1); front.nrm.push(0, 0, 1); front.uv.push(0.5 + 0.5 * c, 0.5 - 0.5 * s);
      back.p.push(r * c, r * s, z0); back.nrm.push(0, 0, -1); back.uv.push(0, 0);
      side.p.push(r * c, r * s, z1, r * c, r * s, z0);
      side.nrm.push(c, s, 0, c, s, 0);
      side.uv.push(0, 0, 0, 0);
      if (i < n) {
        front.idx.push(0, i + 1, i + 2);
        back.idx.push(0, i + 2, i + 1);
        side.idx.push(2 * i, 2 * i + 1, 2 * i + 3, 2 * i, 2 * i + 3, 2 * i + 2);
      }
    }
    m.raw('art', front.p, front.nrm, front.uv, front.idx);
    m.raw('back', back.p, back.nrm, back.uv, back.idx);
    m.raw(mat.wrap ? 'back' : 'edge', side.p, side.nrm, side.uv, side.idx);
  }

  function buildFrame(m, w, h, z1) {
    var x0 = -w / 2, x1 = w / 2, y0 = -h / 2, y1 = h / 2;
    var g = FRAME.gap, f = FRAME.face, zf = z1 + FRAME.lip;
    var ix0 = x0 - g, ix1 = x1 + g, iy0 = y0 - g, iy1 = y1 + g;
    var ox0 = ix0 - f, ox1 = ix1 + f, oy0 = iy0 - f, oy1 = iy1 + f;
    m.box('frame', ox0, ix0, oy0, oy1, 0, zf);
    m.box('frame', ix1, ox1, oy0, oy1, 0, zf);
    m.box('frame', ix0, ix1, iy1, oy1, 0, zf);
    m.box('frame', ix0, ix1, oy0, iy0, 0, zf);
    // bodem van de schaduwvoeg
    m.quad('gap', [[ix0, iy0, 0.001], [ix1, iy0, 0.001], [ix1, iy1, 0.001], [ix0, iy1, 0.001]], [0, 0, 1]);
  }

  function materialColor(name, spec) {
    if (name === 'frame') return COLORS[spec.frame] || COLORS.black;
    if (name === 'edge') return MATERIALS[spec.material].edge || COLORS.white;
    return COLORS[name] || [1, 1, 1];
  }

  function materialRoughness(name, spec) {
    if (name === 'art') return MATERIALS[spec.material].rough;
    if (name === 'frame') return spec.frame === 'oak' || spec.frame === 'blank' ? 0.7 : 0.65;
    if (name === 'edge') return 0.5;
    return 0.9;
  }

  /* ---------- GLB (Android + 3D-voorbeeld) ---------- */

  // imageType: 'image/jpeg' (browser) of 'image/png' (Worker, vrijstaande productfoto's).
  function writeGLB(groups, jpeg, spec, imageType) {
    var chunks = [];
    var byteLength = 0;
    var bufferViews = [];
    var accessors = [];

    function addView(bytes, target) {
      var pad = (4 - (byteLength % 4)) % 4;
      if (pad) { chunks.push(new Uint8Array(pad)); byteLength += pad; }
      var view = { buffer: 0, byteOffset: byteLength, byteLength: bytes.byteLength };
      if (target) view.target = target;
      bufferViews.push(view);
      chunks.push(new Uint8Array(bytes.buffer, bytes.byteOffset, bytes.byteLength));
      byteLength += bytes.byteLength;
      return bufferViews.length - 1;
    }

    function addAccessor(array, type, componentType, target, withBounds) {
      var view = addView(array, target);
      var size = { SCALAR: 1, VEC2: 2, VEC3: 3 }[type];
      var acc = { bufferView: view, componentType: componentType, count: array.length / size, type: type };
      if (withBounds) {
        var min = [Infinity, Infinity, Infinity], max = [-Infinity, -Infinity, -Infinity];
        for (var i = 0; i < array.length; i += 3) {
          for (var k = 0; k < 3; k++) {
            min[k] = Math.min(min[k], array[i + k]);
            max[k] = Math.max(max[k], array[i + k]);
          }
        }
        acc.min = min;
        acc.max = max;
      }
      accessors.push(acc);
      return accessors.length - 1;
    }

    var materials = [];
    var primitives = [];
    Object.keys(groups).forEach(function (name) {
      var g = groups[name];
      var material = {
        name: name,
        pbrMetallicRoughness: { metallicFactor: 0, roughnessFactor: materialRoughness(name, spec) }
      };
      if (name === 'art') material.pbrMetallicRoughness.baseColorTexture = { index: 0 };
      else material.pbrMetallicRoughness.baseColorFactor = materialColor(name, spec).concat(1);
      materials.push(material);
      primitives.push({
        attributes: {
          POSITION: addAccessor(new Float32Array(g.positions), 'VEC3', 5126, 34962, true),
          NORMAL: addAccessor(new Float32Array(g.normals), 'VEC3', 5126, 34962),
          TEXCOORD_0: addAccessor(new Float32Array(g.uvs), 'VEC2', 5126, 34962)
        },
        indices: addAccessor(new Uint16Array(g.indices), 'SCALAR', 5123, 34963),
        material: materials.length - 1
      });
    });
    var imageView = addView(jpeg);

    var json = {
      asset: { version: '2.0', generator: 'ArtifyCanvas AR' },
      scene: 0,
      scenes: [{ nodes: [0] }],
      nodes: [{ mesh: 0, name: 'Canvas' }],
      meshes: [{ primitives: primitives }],
      materials: materials,
      textures: [{ source: 0, sampler: 0 }],
      samplers: [{ magFilter: 9729, minFilter: 9987, wrapS: 33071, wrapT: 33071 }],
      images: [{ bufferView: imageView, mimeType: imageType || 'image/jpeg' }],
      accessors: accessors,
      bufferViews: bufferViews,
      buffers: [{ byteLength: 0 }]
    };
    var binPad = (4 - (byteLength % 4)) % 4;
    if (binPad) { chunks.push(new Uint8Array(binPad)); byteLength += binPad; }
    json.buffers[0].byteLength = byteLength;

    var jsonBytes = new TextEncoder().encode(JSON.stringify(json));
    var jsonPad = (4 - (jsonBytes.length % 4)) % 4;
    var jsonLen = jsonBytes.length + jsonPad;
    var total = 12 + 8 + jsonLen + 8 + byteLength;
    var out = new Uint8Array(total);
    var dv = new DataView(out.buffer);
    dv.setUint32(0, 0x46546c67, true); // 'glTF'
    dv.setUint32(4, 2, true);
    dv.setUint32(8, total, true);
    dv.setUint32(12, jsonLen, true);
    dv.setUint32(16, 0x4e4f534a, true); // 'JSON'
    out.set(jsonBytes, 20);
    for (var p = 0; p < jsonPad; p++) out[20 + jsonBytes.length + p] = 0x20;
    var off = 20 + jsonLen;
    dv.setUint32(off, byteLength, true);
    dv.setUint32(off + 4, 0x004e4942, true); // 'BIN'
    off += 8;
    chunks.forEach(function (c) { out.set(c, off); off += c.length; });
    return new Blob([out], { type: 'model/gltf-binary' });
  }

  /* ---------- USDZ (iPhone / AR Quick Look) ---------- */

  var USD_SCENE = '/Root/Scenes/Scene';

  function fmt(n) { return (Math.round(n * 1e5) / 1e5).toString(); }

  function tuples(arr, size, mapFn) {
    var out = [];
    for (var i = 0; i < arr.length; i += size) {
      var t = arr.slice(i, i + size);
      if (mapFn) t = mapFn(t);
      out.push('(' + t.map(fmt).join(', ') + ')');
    }
    return '[' + out.join(', ') + ']';
  }

  function usdMaterial(name, spec) {
    var path = USD_SCENE + '/Materials/' + name;
    var rough = fmt(materialRoughness(name, spec));
    var lines = [
      '        def Material "' + name + '"',
      '        {',
      '            token outputs:surface.connect = <' + path + '/Surface.outputs:surface>',
      '            def Shader "Surface"',
      '            {',
      '                uniform token info:id = "UsdPreviewSurface"'
    ];
    if (name === 'art') {
      lines.push('                color3f inputs:diffuseColor.connect = <' + path + '/Texture.outputs:rgb>');
    } else {
      lines.push('                color3f inputs:diffuseColor = (' + materialColor(name, spec).map(fmt).join(', ') + ')');
    }
    lines.push(
      '                float inputs:metallic = 0',
      '                float inputs:roughness = ' + rough,
      '                token outputs:surface',
      '            }'
    );
    if (name === 'art') {
      lines.push(
        '            def Shader "UVReader"',
        '            {',
        '                uniform token info:id = "UsdPrimvarReader_float2"',
        '                token inputs:varname = "st"',
        '                float2 outputs:result',
        '            }',
        '            def Shader "Texture"',
        '            {',
        '                uniform token info:id = "UsdUVTexture"',
        '                asset inputs:file = @textures/art.jpg@',
        '                float2 inputs:st.connect = <' + path + '/UVReader.outputs:result>',
        '                token inputs:sourceColorSpace = "sRGB"',
        '                token inputs:wrapS = "clamp"',
        '                token inputs:wrapT = "clamp"',
        '                float3 outputs:rgb',
        '            }'
      );
    }
    lines.push('        }');
    return lines.join('\n');
  }

  function writeUSDA(groups, spec) {
    var names = Object.keys(groups);
    var meshes = names.map(function (name) {
      var g = groups[name];
      var counts = [];
      for (var i = 0; i < g.indices.length / 3; i++) counts.push(3);
      return [
        '    def Mesh "' + name + '" (',
        '        prepend apiSchemas = ["MaterialBindingAPI"]',
        '    )',
        '    {',
        '        int[] faceVertexCounts = [' + counts.join(', ') + ']',
        '        int[] faceVertexIndices = [' + g.indices.join(', ') + ']',
        '        rel material:binding = <' + USD_SCENE + '/Materials/' + name + '>',
        '        normal3f[] normals = ' + tuples(g.normals, 3) + ' (',
        '            interpolation = "vertex"',
        '        )',
        '        point3f[] points = ' + tuples(g.positions, 3),
        '        texCoord2f[] primvars:st = ' + tuples(g.uvs, 2, function (t) { return [t[0], 1 - t[1]]; }) + ' (',
        '            interpolation = "vertex"',
        '        )',
        '        uniform token orientation = "rightHanded"',
        '        uniform token subdivisionScheme = "none"',
        '    }'
      ].join('\n');
    });
    return [
      '#usda 1.0',
      '(',
      '    customLayerData = {',
      '        string creator = "ArtifyCanvas AR"',
      '    }',
      '    defaultPrim = "Root"',
      '    metersPerUnit = 1',
      '    upAxis = "Y"',
      ')',
      '',
      // AR Quick Look leest de verankering alleen uit een scene in een "sceneLibrary".
      'def Xform "Root"',
      '{',
      '  def Scope "Scenes" (',
      '    kind = "sceneLibrary"',
      '  )',
      '  {',
      '    def Xform "Scene" (',
      '      customData = {',
      '        bool preliminary_collidesWithEnvironment = 0',
      '        string sceneName = "Scene"',
      '      }',
      '      sceneName = "Scene"',
      '    )',
      '    {',
      '    token preliminary:anchoring:type = "plane"',
      '    token preliminary:planeAnchoring:alignment = "vertical"',
      '',
      // Bij een muuranker is de y-as van Quick Look de normaal van de muur (van de muur af)
      // en wijst -z omhoog. Draai -90° om x: voorkant (+z) → van de muur af, boven (+y) → omhoog.
      '    def Xform "Artwork"',
      '    {',
      '    float3 xformOp:rotateXYZ = (-90, 0, 0)',
      '    uniform token[] xformOpOrder = ["xformOp:rotateXYZ"]',
      '',
      meshes.join('\n\n'),
      '    }',
      '',
      '    def Scope "Materials"',
      '    {',
      names.map(function (n) { return usdMaterial(n, spec); }).join('\n'),
      '    }',
      '    }',
      '  }',
      '}',
      ''
    ].join('\n');
  }

  var CRC_TABLE = (function () {
    var t = new Uint32Array(256);
    for (var n = 0; n < 256; n++) {
      var c = n;
      for (var k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
      t[n] = c >>> 0;
    }
    return t;
  })();

  function crc32(bytes) {
    var c = 0xffffffff;
    for (var i = 0; i < bytes.length; i++) c = CRC_TABLE[(c ^ bytes[i]) & 0xff] ^ (c >>> 8);
    return (c ^ 0xffffffff) >>> 0;
  }

  // USDZ = ongecomprimeerde zip waarin elke bestandsinhoud op een 64-byte grens begint.
  function writeUSDZ(files) {
    var parts = [];
    var central = [];
    var offset = 0;
    files.forEach(function (file) {
      var name = new TextEncoder().encode(file.name);
      var data = file.data;
      var crc = crc32(data);
      var headerLen = 30 + name.length;
      var extraLen = (64 - ((offset + headerLen + 4) % 64)) % 64 + 4; // 4 = extra-veld header
      var header = new Uint8Array(headerLen + extraLen);
      var dv = new DataView(header.buffer);
      dv.setUint32(0, 0x04034b50, true);
      dv.setUint16(4, 20, true);
      dv.setUint16(8, 0, true); // stored
      dv.setUint32(14, crc, true);
      dv.setUint32(18, data.length, true);
      dv.setUint32(22, data.length, true);
      dv.setUint16(26, name.length, true);
      dv.setUint16(28, extraLen, true);
      header.set(name, 30);
      dv.setUint16(30 + name.length, 0x1986, true); // padding-veld (genegeerd door lezers)
      dv.setUint16(32 + name.length, extraLen - 4, true);
      central.push({ name: name, crc: crc, size: data.length, offset: offset });
      parts.push(header, data);
      offset += header.length + data.length;
    });
    var cdStart = offset;
    central.forEach(function (e) {
      var rec = new Uint8Array(46 + e.name.length);
      var dv = new DataView(rec.buffer);
      dv.setUint32(0, 0x02014b50, true);
      dv.setUint16(4, 20, true);
      dv.setUint16(6, 20, true);
      dv.setUint16(10, 0, true);
      dv.setUint32(16, e.crc, true);
      dv.setUint32(20, e.size, true);
      dv.setUint32(24, e.size, true);
      dv.setUint16(28, e.name.length, true);
      dv.setUint32(42, e.offset, true);
      rec.set(e.name, 46);
      parts.push(rec);
      offset += rec.length;
    });
    var end = new Uint8Array(22);
    var edv = new DataView(end.buffer);
    edv.setUint32(0, 0x06054b50, true);
    edv.setUint16(8, central.length, true);
    edv.setUint16(10, central.length, true);
    edv.setUint32(12, offset - cdStart, true);
    edv.setUint32(16, cdStart, true);
    parts.push(end);
    return new Blob(parts, { type: 'model/vnd.usdz+zip' });
  }

  /* ---------- modellen bouwen ---------- */

  // URL voor Google Scene Viewer (Android): de Worker bouwt hetzelfde model op de server,
  // want Scene Viewer kan geen model uit de browser (blob) openen.
  function sceneViewerModelUrl(endpoint, imageUrl, parsed, size, crop, bitmap) {
    if (!endpoint) return null;
    var src = new URL(imageUrl, location.href);
    var round = function (n) { return Math.round(n * 10000) / 10000; };
    var params = new URLSearchParams({
      src: src.origin + src.pathname,
      shape: parsed.shape,
      w: round(size.w),
      h: round(size.h),
      frame: parsed.frame,
      mat: parsed.material,
      crop: [crop.x / bitmap.width, crop.y / bitmap.height, crop.w / bitmap.width, crop.h / bitmap.height]
        .map(function (n) { return Math.min(1, Math.max(0, round(n))); }).join(',')
    });
    return endpoint + '?' + params.toString();
  }

  function buildModels(imageUrl, parsed, endpoint) {
    var key = [imageUrl, parsed.shape, parsed.size.w, parsed.size.h, parsed.frame, parsed.material].join('|');
    if (cache[key]) return cache[key];
    cache[key] = loadBitmap(imageUrl).then(function (bitmap) {
      var size = orientSize(parsed.size, bitmap.width, bitmap.height);
      var crop = coverCrop(bitmap, size.w / size.h, parsed.shape === 'rect');
      return buildTexture(bitmap, size.w / size.h, crop).then(function (jpeg) {
        var groups = buildGeometry(parsed, size.w, size.h);
        var usda = new TextEncoder().encode(writeUSDA(groups, parsed));
        return {
          size: size,
          sceneViewer: sceneViewerModelUrl(endpoint, imageUrl, parsed, size, crop, bitmap),
          glb: URL.createObjectURL(writeGLB(groups, jpeg, parsed)),
          usdz: URL.createObjectURL(writeUSDZ([
            { name: 'model.usda', data: usda },
            { name: 'textures/art.jpg', data: jpeg }
          ]))
        };
      });
    });
    cache[key].catch(function () { delete cache[key]; });
    return cache[key];
  }

  function loadModelViewer() {
    if (customElements.get('model-viewer')) return Promise.resolve();
    if (!modelViewerPromise) {
      modelViewerPromise = new Promise(function (resolve, reject) {
        var s = document.createElement('script');
        s.type = 'module';
        s.src = MODEL_VIEWER_SRC;
        s.onload = function () { customElements.whenDefined('model-viewer').then(resolve); };
        s.onerror = function () { modelViewerPromise = null; reject(new Error('3D-viewer laden mislukt')); };
        document.head.appendChild(s);
      });
    }
    return modelViewerPromise;
  }

  /* ---------- UI ---------- */

  function readData(root) {
    var el = root.querySelector('[data-ac-ar-data]');
    try { return JSON.parse(el.textContent); } catch (err) { return null; }
  }

  // Neemt het eerste winkelwagenformulier dat een variant van dít product bevat
  // (upsell-formulieren op dezelfde pagina worden zo genegeerd).
  function currentVariant(root, data) {
    var byId = function (id) {
      return data.variants.filter(function (v) { return String(v.id) === String(id); })[0];
    };
    var scope = root.closest('.shopify-section') || document;
    var inputs = Array.prototype.slice.call(scope.querySelectorAll('form[action*="/cart/add"] [name="id"]'))
      .concat(Array.prototype.slice.call(document.querySelectorAll('form[action*="/cart/add"] [name="id"]')));
    for (var i = 0; i < inputs.length; i++) {
      if (byId(inputs[i].value)) return byId(inputs[i].value);
    }
    return byId(new URL(location.href).searchParams.get('variant')) || byId(data.selected) || data.variants[0];
  }

  // Variantfoto's zijn soms sfeerfoto's (kamer, lijst op de muur). Gebruik de variantfoto alleen
  // als de verhouding beter bij het formaat past dan de hoofdfoto; anders de hoofdfoto (kale print).
  function pickImage(variant, data, parsed) {
    if (!variant.image) return data.image;
    if (!variant.ratio || !data.ratio) return variant.image;
    var norm = function (r) { return r > 1 ? 1 / r : r; };
    var target = norm(parsed.size.w / parsed.size.h);
    var diffVariant = Math.abs(norm(variant.ratio) - target);
    var diffMain = Math.abs(norm(data.ratio) - target);
    return diffVariant + 0.02 < diffMain ? variant.image : data.image;
  }

  function describe(parsed, size, t) {
    var cm = function (m) { return Math.round(m * 100); };
    var parts = [parsed.shape === 'round' ? 'Ø ' + cm(size.w) + ' cm' : cm(size.w) + ' × ' + cm(size.h) + ' cm'];
    var frame = parsed.frame === 'none' ? t.noFrame : (t.frames || {})[parsed.frame];
    if (parsed.shape === 'rect' && frame) parts.push(frame);
    return parts.join(' · ');
  }

  function track(name, detail) {
    try {
      if (window.dataLayer) window.dataLayer.push(Object.assign({ event: name }, detail));
      if (window.Shopify && Shopify.analytics && Shopify.analytics.publish) Shopify.analytics.publish(name, detail);
    } catch (err) { /* tracking mag de AR nooit blokkeren */ }
  }

  function createDialog(t) {
    var dialog = document.createElement('dialog');
    dialog.className = 'ac-ar-dialog';
    dialog.setAttribute('aria-label', t.title);
    dialog.innerHTML =
      '<div class="ac-ar-dialog__head">' +
      '  <div><p class="ac-ar-dialog__title"></p><p class="ac-ar-dialog__meta"></p></div>' +
      '  <button type="button" class="ac-ar-dialog__close" aria-label="' + t.close + '">&times;</button>' +
      '</div>' +
      '<div class="ac-ar-dialog__stage"><p class="ac-ar-dialog__status" role="status"></p></div>' +
      '<div class="ac-ar-dialog__hint" aria-live="polite"></div>';
    dialog.querySelector('.ac-ar-dialog__close').addEventListener('click', function () { dialog.close(); });
    dialog.addEventListener('click', function (e) { if (e.target === dialog) dialog.close(); });
    document.body.appendChild(dialog);
    return dialog;
  }

  var IS_ANDROID = /android/i.test(navigator.userAgent);

  // Opent Google Scene Viewer (Android) met verticale plaatsing en vaste ware grootte.
  function sceneViewerIntent(fileUrl, title) {
    var params = new URLSearchParams({
      file: fileUrl,
      mode: 'ar_preferred',
      resizable: 'false',
      enable_vertical_placement: 'true',
      disable_occlusion: 'true',
      title: title
    });
    var fallback = encodeURIComponent(location.href.split('#')[0]);
    return 'intent://arvr.google.com/scene-viewer/1.0?' + params.toString() +
      '#Intent;scheme=https;package=com.google.android.googlequicksearchbox;' +
      'action=android.intent.action.VIEW;S.browser_fallback_url=' + fallback + ';end;';
  }

  // Uitleg vóór het openen van AR: mikken gaat makkelijker dan slepen, want Quick Look en
  // Scene Viewer plaatsen het werk waar het midden van het scherm op de muur wijst.
  function renderSteps(hint, t) {
    hint.textContent = '';
    if (!t.steps) {
      hint.textContent = t.hintAr;
      return;
    }
    var list = document.createElement('ol');
    list.className = 'ac-ar-dialog__steps';
    t.steps.concat(IS_ANDROID ? t.redoAndroid : t.redoIos).forEach(function (text) {
      var item = document.createElement('li');
      item.textContent = text;
      list.appendChild(item);
    });
    hint.appendChild(list);
  }

  function createViewer(models, t, title) {
    var useSceneViewer = IS_ANDROID && !!models.sceneViewer;
    var mv = document.createElement('model-viewer');
    mv.setAttribute('src', models.glb);
    mv.setAttribute('ios-src', models.usdz);
    mv.setAttribute('loading', 'eager');
    mv.setAttribute('reveal', 'auto');
    if (!useSceneViewer) {
      mv.setAttribute('ar', '');
      mv.setAttribute('ar-modes', 'webxr quick-look');
    }
    mv.setAttribute('ar-placement', 'wall');
    mv.setAttribute('ar-scale', 'fixed');
    mv.setAttribute('camera-controls', '');
    mv.setAttribute('touch-action', 'pan-y');
    mv.setAttribute('camera-orbit', '0deg 90deg auto');
    mv.setAttribute('min-camera-orbit', '-60deg 30deg auto');
    mv.setAttribute('max-camera-orbit', '60deg 150deg auto');
    mv.setAttribute('interaction-prompt', 'none');
    mv.setAttribute('shadow-intensity', '0.6');
    mv.setAttribute('environment-image', 'neutral');
    mv.setAttribute('alt', t.title);
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'ac-ar-dialog__ar';
    btn.textContent = t.place;
    if (useSceneViewer) {
      btn.addEventListener('click', function () {
        track('ar_wall_session', { mode: 'scene-viewer' });
        location.href = sceneViewerIntent(models.sceneViewer, title);
      });
    } else {
      btn.slot = 'ar-button';
    }
    mv.appendChild(btn);
    mv._acSceneViewer = useSceneViewer;
    return mv;
  }

  function open(root) {
    var data = readData(root);
    if (!data) return;
    var t = data.i18n;
    var variant = currentVariant(root, data);
    var parsed = parseVariant(variant.options, data.hint);
    if (!parsed.size) return;

    var dialog = root._acDialog || (root._acDialog = createDialog(t));
    var stage = dialog.querySelector('.ac-ar-dialog__stage');
    var status = dialog.querySelector('.ac-ar-dialog__status');
    var hint = dialog.querySelector('.ac-ar-dialog__hint');
    var old = stage.querySelector('model-viewer');
    if (old) old.remove();
    dialog.querySelector('.ac-ar-dialog__title').textContent = data.title;
    dialog.querySelector('.ac-ar-dialog__meta').textContent = '';
    status.textContent = t.loading;
    status.hidden = false;
    hint.textContent = '';
    if (!dialog.open) dialog.showModal();

    track('ar_wall_open', { product_title: data.title, variant_id: variant.id });

    Promise.all([buildModels(pickImage(variant, data, parsed), parsed, data.glbEndpoint), loadModelViewer()])
      .then(function (res) {
        var models = res[0];
        dialog.querySelector('.ac-ar-dialog__meta').textContent = describe(parsed, models.size, t);
        var mv = createViewer(models, t, data.title);
        // Laat de server het Android-model alvast maken, zodat Scene Viewer direct opent.
        if (mv._acSceneViewer) fetch(models.sceneViewer, { mode: 'cors' }).catch(function () {});
        mv.addEventListener('load', function () {
          status.hidden = true;
          if (mv.canActivateAR || mv._acSceneViewer) renderSteps(hint, t);
          else hint.textContent = t.hintDesktop;
        });
        mv.addEventListener('ar-status', function (e) {
          if (e.detail.status === 'session-started') track('ar_wall_session', { variant_id: variant.id });
          if (e.detail.status === 'failed') hint.textContent = t.failed;
        });
        mv.addEventListener('error', function () { status.textContent = t.failed; });
        stage.appendChild(mv);
      })
      .catch(function (err) {
        console.error('[AR muurweergave]', err);
        status.textContent = t.failed;
      });
  }

  function init() {
    document.querySelectorAll('[data-ac-ar]').forEach(function (root) {
      var data = readData(root);
      var ok = data && data.variants.some(function (v) { return parseVariant(v.options, data.hint).size; }) &&
        'createImageBitmap' in window && typeof HTMLDialogElement === 'function';
      root.hidden = !ok;
    });
  }

  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-ac-ar-open]');
    if (!btn) return;
    e.preventDefault();
    open(btn.closest('[data-ac-ar]'));
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:load', init);
})();
